import { useCallback, useRef, useState } from 'react';
import { toast } from '../../Components/Toast';
import { createUnitReportShare } from '../../services/metaAdsService';
import { UNIT_REPORT_SHARE_MAX_DAYS, getMetaErrorMessage, readMetaErrorCode } from '../metaAdsConfig';
import { formatMetaDay } from '../metaPeriod';
import { buildUnitReportShareUrl, readUnitReportShare } from '../metaUnitReport';

/*
 * «Copiar link» (t1057u / t1059u): sin sección de compartir. Un clic crea el
 * link con el vencimiento más largo (90 días), lo copia y avisa hasta cuándo vale. Sin revocar ni
 * reemplazar: la API sigue teniendo vencimiento y revocación, el Front no los muestra.
 *
 * - Mientras el diálogo está abierto y el período no cambia, se reusa el último link creado (en
 *   memoria; el link completo solo sale una vez de la API y no se guarda en ningún lado).
 * - Safari: el portapapeles exige el gesto del usuario; se escribe con `ClipboardItem` + una promesa
 *   (el link llega después del pedido). Si el navegador no deja, toast con «Copiar» (nuevo gesto).
 * - 409 `UNIT_REPORT_SHARE_LIMIT`: solo un aviso (no se revoca nada).
 */

export const UNIT_REPORT_LINK_COPIED = (until) => (until ? `Link copiado · vale hasta el ${until}` : 'Link copiado');
export const UNIT_REPORT_LINK_COPY_FAILED = 'No pudimos copiar el link';
export const UNIT_REPORT_LINK_LIMIT = 'Esta propiedad ya tiene 10 links activos. Se liberan solos cuando vencen.';

/** Vencimiento (ISO) → día de Córdoba «28 ene» (no el día UTC). */
export function unitReportLinkUntil(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  try {
    const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Cordoba', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    return formatMetaDay(day);
  } catch {
    return formatMetaDay(iso);
  }
}

class ShareCreateError extends Error {
  constructor(res) {
    super('share-create');
    this.res = res;
  }
}

/** Copia con un gesto nuevo (botón «Copiar» del toast): `writeText` y, si no, `execCommand`. */
export async function copyTextNow(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // sigue con el método viejo
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = typeof document.execCommand === 'function' && document.execCommand('copy');
    document.body.removeChild(el);
    return Boolean(ok);
  } catch {
    return false;
  }
}

/**
 * Escribe en el portapapeles un texto que llega después (promesa), dentro del gesto del clic.
 * Con `ClipboardItem` (Safari, Chrome) la promesa va adentro; sin él, se espera y `writeText`.
 */
export async function copyTextLater(textPromise) {
  const canItem = typeof window !== 'undefined' && typeof window.ClipboardItem === 'function' && navigator?.clipboard?.write;
  if (canItem) {
    const blob = textPromise.then((t) => new Blob([t], { type: 'text/plain' }));
    // Si falla el pedido, la promesa del blob también: lo maneja quien llama (`textPromise`).
    blob.catch(() => {});
    await navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': blob })]);
    return;
  }
  const text = await textPromise;
  await navigator.clipboard.writeText(text);
}

/** @returns {{ copyLink: () => Promise<void>, copying: boolean }} */
export function useUnitReportShareLink({ itemKind, recordId, ownerUserId = null, period }) {
  const [copying, setCopying] = useState(false);
  // Último link creado en este diálogo: `{ key, url, expiresAt }` (key = período).
  const lastRef = useRef(null);
  const periodKey = `${period?.since || ''}|${period?.until || ''}`;

  const createLink = useCallback(async () => {
    const res = await createUnitReportShare({
      itemKind, id: recordId, period, expiresInDays: UNIT_REPORT_SHARE_MAX_DAYS, ownerUserId,
    });
    if (!res?._httpOk) throw new ShareCreateError(res);
    const share = readUnitReportShare(res.data);
    const url = share.token ? buildUnitReportShareUrl(share.token) : '';
    if (!url) throw new ShareCreateError(res);
    lastRef.current = { key: periodKey, url, expiresAt: share.expiresAt };
    return url;
  }, [itemKind, recordId, period, ownerUserId, periodKey]);

  const copyLink = useCallback(async () => {
    if (copying) return;
    const cached = lastRef.current && lastRef.current.key === periodKey ? lastRef.current : null;
    setCopying(true);
    const urlPromise = cached ? Promise.resolve(cached.url) : createLink();
    let created = null;
    try {
      created = await Promise.all([urlPromise, copyTextLater(urlPromise)]).then(([url]) => url);
    } catch (err) {
      setCopying(false);
      let url = null;
      try { url = await urlPromise; } catch { url = null; }
      if (!url) {
        const res = err instanceof ShareCreateError ? err.res : null;
        if (res && readMetaErrorCode(res) === 'UNIT_REPORT_SHARE_LIMIT') {
          toast.warning({ message: UNIT_REPORT_LINK_LIMIT });
        } else {
          toast.error({ title: 'No pudimos crear el link', message: getMetaErrorMessage(res) });
        }
        return;
      }
      // El link existe pero el navegador no dejó copiar: «Copiar» con un gesto nuevo.
      toast.error({
        message: UNIT_REPORT_LINK_COPY_FAILED,
        actionLabel: 'Copiar',
        duration: 10000,
        onAction: async () => {
          const ok = await copyTextNow(url);
          if (ok) toast.success({ message: UNIT_REPORT_LINK_COPIED(unitReportLinkUntil(lastRef.current?.expiresAt)) });
          else toast.error({ message: UNIT_REPORT_LINK_COPY_FAILED });
        },
      });
      return;
    }
    setCopying(false);
    if (created) toast.success({ message: UNIT_REPORT_LINK_COPIED(unitReportLinkUntil(lastRef.current?.expiresAt)) });
  }, [copying, periodKey, createLink]);

  return { copyLink, copying };
}
